import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useDownload, FILE_STATUS } from "./hooks/useDownload";
import {
  DownloadIcon,
  RetryIcon,
  CheckCircleFilledIcon,
  ErrorCircleFilledIcon,
  ZipFileIcon,
  VideoFileIcon,
  SubtitleFileIcon,
  GenericFileIcon,
  InfoIcon,
  ClockIcon,
  AlertCircleIcon,
  CheckCircleOutlineIcon,
  WarnTriangleIcon,
  PlusIcon,
  HomeIcon,
  ArrowLeftIcon,
} from "../../icons";
import "./DownloadCard.scss";

// Per-file spinner (CSS-only, not an SVG icon)
const IcoFileSpinner = () => <span className="dc-file-spinner" aria-label="Downloading" />;

function FileTypeIcon({ filename = "" }) {
  const ext = filename.split(".").pop().toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return <VideoFileIcon />;
  if (["srt", "vtt", "ass", "sub"].includes(ext)) return <SubtitleFileIcon />;
  if (ext === "zip") return <ZipFileIcon />;
  return <GenericFileIcon />;
}

function fileIconClass(filename = "") {
  const ext = filename.split(".").pop().toLowerCase();
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext)) return "dc-file-icon--video";
  if (["srt", "vtt", "ass", "sub"].includes(ext)) return "dc-file-icon--subtitle";
  return "dc-file-icon--generic";
}

function fileTypeLabel(filename = "") {
  const ext = filename.split(".").pop().toLowerCase();
  if (["mp4", "mov", "avi", "mkv"].includes(ext)) return `${ext.toUpperCase()} Video`;
  if (ext === "srt") return "SubRip Subtitle";
  if (ext === "vtt") return "WebVTT Subtitle";
  if (ext === "zip") return "ZIP Archive";
  return ext.toUpperCase() + " File";
}

function FileBadge({ status, error, onRetry, isRetrying }) {
  switch (status) {
    case FILE_STATUS.DOWNLOADING:
      return (
        <span className="dc-file-badge dc-file-badge--downloading">
          <IcoFileSpinner /> Downloading
        </span>
      );
    case FILE_STATUS.DONE:
      return (
        <span className="dc-file-badge dc-file-badge--done">
          <CheckCircleFilledIcon size={17} /> Saved
        </span>
      );
    case FILE_STATUS.ERROR:
      return (
        <span className="dc-file-badge-error-wrap">
          <span className="dc-file-badge dc-file-badge--error" title={error}>
            <ErrorCircleFilledIcon size={16} /> Failed
          </span>
          <button
            type="button"
            className="dc-file-retry-btn"
            onClick={onRetry}
            disabled={isRetrying}
            aria-label="Retry this file"
            title={error || "Retry download"}
          >
            {isRetrying ? (
              <>
                <IcoFileSpinner /> Retrying…
              </>
            ) : (
              <>
                <RetryIcon size={17} /> Retry
              </>
            )}
          </button>
        </span>
      );
    default: // PENDING
      return <span className="dc-file-badge dc-file-badge--pending">Queued</span>;
  }
}

function DownloadCard({ videoFileName = "AV_Sync_Output", jobId, onBack }) {
  const navigate = useNavigate();
  const {
    startDownload,
    retryFile,
    isFetching,
    isDownloading,
    isBusy,
    isDownloaded,
    isError,
    downloadError,
    fileList,
    summary,
    hasPartialFailure,
  } = useDownload(videoFileName, jobId);

  // Track which single file is currently being retried
  const [retryingFile, setRetryingFile] = useState(null);

  const handleFileRetry = useCallback(
    async (filename) => {
      if (retryingFile) return; // prevent overlapping retries
      setRetryingFile(filename);
      await retryFile(filename);
      setRetryingFile(null);
    },
    [retryingFile, retryFile]
  );

  const headerIconClass = isError
    ? "dc-page-header-icon--error"
    : isDownloaded
      ? "dc-page-header-icon--done"
      : "";

  const headerIcon = isError ? (
    <AlertCircleIcon size={28} />
  ) : isDownloaded ? (
    <CheckCircleOutlineIcon size={28} />
  ) : (
    <DownloadIcon size={28} />
  );

  const pageTitle = isDownloaded
    ? hasPartialFailure
      ? "Download Partially Complete"
      : "Download Complete"
    : isError
      ? "Download Failed"
      : isFetching
        ? "Preparing Downloads…"
        : "Download Output Files";

  const pageSub = isDownloaded
    ? hasPartialFailure
      ? `${summary.succeeded} of ${summary.succeeded + summary.failed} file(s) saved. Retry to attempt failed files.`
      : `All ${summary?.succeeded ?? fileList.length} file(s) saved to your device.`
    : isError
      ? "An error occurred. Check the message below and retry."
      : isFetching
        ? "Fetching your output file list from the server…"
        : "Your synchronised output files are ready to download.";

  const btnLabel = isFetching ? (
    <>
      <span className="dc-spinner" /> Fetching files…
    </>
  ) : isDownloading ? (
    <>
      <span className="dc-spinner" /> Downloading…
    </>
  ) : isError || hasPartialFailure ? (
    <>
      <RetryIcon size={17} /> Retry Download
    </>
  ) : isDownloaded ? (
    <>
      <CheckCircleFilledIcon size={17} /> All Files Downloaded
    </>
  ) : (
    <>
      <DownloadIcon size={17} /> Download All Files
    </>
  );

  const btnDisabled = isBusy || (isDownloaded && !hasPartialFailure);

  const doneCount = fileList.filter((f) => f.status === FILE_STATUS.DONE).length;
  const totalCount = fileList.length;
  const showProgress = (isBusy || isDownloaded) && totalCount > 0;

  return (
    <div className="dc-root">
      {/* ── Page Header ───────────────────────────────────────────────── */}
      <div className="dc-page-header">
        <div className={`dc-page-header-icon ${headerIconClass}`}>
          {isBusy ? <span className="dc-header-spinner" /> : headerIcon}
        </div>
        <div className="dc-page-header-text">
          <h1 className="dc-page-title">{pageTitle}</h1>
          <p className="dc-page-sub">{pageSub}</p>
        </div>
      </div>

      {/* ── Patience / Dashboard Disclaimer — always visible before completion ── */}
      {!isDownloaded && (
        <div className="dc-patience-disclaimer" role="note">
          <ClockIcon />
          <div className="dc-patience-body">
            <strong>⏱ Please be patient</strong>
            <span>
              Larger files may take several minutes to download — please wait while your file saves
              to your device. You can also access and download your output files anytime from the{" "}
              <button
                type="button"
                className="dc-disclaimer-link"
                onClick={() => navigate("/dashboard")}
              >
                Dashboard
              </button>
              .
            </span>
          </div>
        </div>
      )}

      {/* ── Deletion Disclaimer — always visible once file list shown ──── */}
      {(fileList.length > 0 || isDownloaded) && (
        <div className="dc-deletion-disclaimer" role="note">
          <WarnTriangleIcon size={14} />
          <span>
            <strong>Important:</strong> Output files are automatically deleted from our servers
            after <strong>It downloaded successfully</strong>. Please save your files to your device
            before they expire.
          </span>
        </div>
      )}

      {/* ── Overall progress bar (shown while downloading) ────────────── */}
      {isBusy && totalCount > 0 && (
        <div
          className="dc-progress-bar-wrap"
          role="progressbar"
          aria-valuenow={doneCount}
          aria-valuemax={totalCount}
        >
          <div
            className="dc-progress-bar-fill"
            style={{ width: `${Math.round((doneCount / totalCount) * 100)}%` }}
          />
        </div>
      )}

      {/* ── Fatal error alert ─────────────────────────────────────────── */}
      {isError && (
        <div className="dc-alert dc-alert--error" role="alert">
          <ErrorCircleFilledIcon size={16} />
          <div className="dc-alert-body">
            <strong>Download failed</strong>
            <span>{downloadError}</span>
          </div>
        </div>
      )}

      {/* ── Partial failure warning ───────────────────────────────────── */}
      {hasPartialFailure && (
        <div className="dc-alert dc-alert--warn" role="alert">
          <AlertCircleIcon size={28} />
          <div className="dc-alert-body">
            <strong>{summary.failed} file(s) failed to download</strong>
            <span>
              The other {summary.succeeded} file(s) saved successfully. Click Retry to try the
              failed file(s) again.
            </span>
          </div>
        </div>
      )}

      {/* ── Output Files Card ─────────────────────────────────────────── */}
      <div className="dc-card">
        {/* Card title + live counter */}
        <div className="dc-card-header-row">
          <h2 className="dc-card-title">Output Files</h2>
          {showProgress && (
            <span className="dc-card-counter">
              {doneCount} / {totalCount} downloaded
            </span>
          )}
        </div>

        {/* ── Empty state (before fetch) ────────────────────────────── */}
        {!isBusy && !isDownloaded && !isError && fileList.length === 0 && (
          <div className="dc-note dc-note--green">
            <InfoIcon size={14} />
            <span>
              Click the button below to fetch and download all your synchronised output files to
              your device.
            </span>
          </div>
        )}

        {/* ── Fetching skeleton rows ────────────────────────────────── */}
        {isFetching && (
          <div className="dc-skeleton-list">
            {[1, 2, 3].map((i) => (
              <div key={i} className="dc-skeleton-row">
                <div className="dc-skeleton dc-skeleton--icon" />
                <div className="dc-skeleton-info">
                  <div className="dc-skeleton dc-skeleton--name" />
                  <div className="dc-skeleton dc-skeleton--meta" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── File list ─────────────────────────────────────────────── */}
        {fileList.length > 0 && (
          <ul className="dc-file-list" aria-label="Output files">
            {fileList.map(({ filename, status, error }) => (
              <li key={filename} className={`dc-file-row dc-file-row--${status}`}>
                <div className={`dc-file-icon ${fileIconClass(filename)}`}>
                  <FileTypeIcon filename={filename} />
                </div>
                <div className="dc-file-info" data-tooltip={filename}>
                  <div className="dc-file-name">{filename}</div>
                  <div className="dc-file-meta">{fileTypeLabel(filename)}</div>
                </div>
                <FileBadge
                  status={status}
                  error={error}
                  onRetry={() => handleFileRetry(filename)}
                  isRetrying={retryingFile === filename}
                />
              </li>
            ))}
          </ul>
        )}

        {/* ── All done success note ─────────────────────────────────── */}
        {isDownloaded && !hasPartialFailure && (
          <div className="dc-note dc-note--green">
            <CheckCircleFilledIcon size={17} />
            <span>All files have been saved successfully to your device.</span>
          </div>
        )}

        {/* ── Card Footer — Back + Download ─────────────────────── */}
        <div className="dc-card-footer">
          {onBack && (
            <button
              type="button"
              className="dc-back-btn"
              onClick={onBack}
              aria-label="Back to Processing"
            >
              <ArrowLeftIcon size={15} />
              Back
            </button>
          )}
          <button
            type="button"
            className="dc-download-btn"
            onClick={startDownload}
            disabled={btnDisabled}
            aria-label="Download all output files"
          >
            {btnLabel}
          </button>
        </div>
      </div>

      {/* ── Go to Dashboard — shown when all files downloaded ─────────── */}

      {isDownloaded && !hasPartialFailure && (
        <div className="dc-all-done-section">
          {/* Completion summary strip */}
          <div className="dc-completion-summary">
            <div className="dc-summary-item">
              <span className="dc-summary-value">{summary?.succeeded ?? fileList.length}</span>
              <span className="dc-summary-label">Files Saved</span>
            </div>
            <div className="dc-summary-divider" />
            <div className="dc-summary-item">
              <span className="dc-summary-value">
                {
                  [
                    ...new Set(
                      fileList.map((f) => {
                        const parts = f.filename.replace(/\.[^.]+$/, "").split("_");
                        return parts[parts.length - 1];
                      })
                    ),
                  ].length
                }
              </span>
              <span className="dc-summary-label">Languages</span>
            </div>
            <div className="dc-summary-divider" />
            <div className="dc-summary-item">
              <span className="dc-summary-value dc-summary-value--green">✓ Done</span>
              <span className="dc-summary-label">Status</span>
            </div>
          </div>

          <p className="dc-all-done-message">
            🎉 All files downloaded successfully! What would you like to do next?
          </p>

          {/* CTA row */}
          <div className="dc-done-cta-row">
            <button
              type="button"
              className="dc-new-job-btn"
              onClick={() => navigate("/services")}
              aria-label="Create a new sync job"
            >
              <PlusIcon size={17} />
              Create New Job
            </button>
            <button
              type="button"
              className="dc-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              aria-label="Go to Dashboard"
            >
              <HomeIcon size={17} />
              Go to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

FileTypeIcon.propTypes = {
  filename: PropTypes.string,
};

FileBadge.propTypes = {
  status: PropTypes.string,
  error: PropTypes.string,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
};

DownloadCard.propTypes = {
  videoFileName: PropTypes.string,
  jobId: PropTypes.string,
  onBack: PropTypes.func,
};

export default DownloadCard;
